// HTMLMediaElement.error.code values (mirrors the DOM MediaError constants,
// spelled out so the classification logic stays unit-testable in jsdom where
// the global `MediaError` may be absent).
export const MEDIA_ERR_ABORTED = 1;
export const MEDIA_ERR_NETWORK = 2;
export const MEDIA_ERR_DECODE = 3;
export const MEDIA_ERR_SRC_NOT_SUPPORTED = 4;

/**
 * How many load attempts a video error of this code warrants before we give up
 * and show the fallback panel.
 *
 * Decode / unsupported-source are (almost) always genuinely fatal — allow a
 * single extra attempt to absorb a one-off decode hiccup, then stop. Network /
 * aborted (and unknown) errors are frequently transient — especially under
 * Firefox's eager metadata loads — so retry a few times before declaring the
 * stream unrenderable.
 */
export const retryLimitForVideoError = (code: number | undefined): number => {
  switch (code) {
    case MEDIA_ERR_SRC_NOT_SUPPORTED:
    case MEDIA_ERR_DECODE:
      return 1;
    default:
      return 3;
  }
};

/**
 * Whether a video that has already failed `attempts` times, most recently with
 * `code`, should be retried once more (vs. showing the fallback).
 */
export const shouldRetryVideoError = (code: number | undefined, attempts: number): boolean =>
  attempts < retryLimitForVideoError(code);

/** Short backoff (ms) before the Nth retry — bounded, no cache-buster. */
export const VIDEO_RETRY_DELAYS = [400, 900, 1800];

export const videoRetryDelay = (attempt: number): number =>
  VIDEO_RETRY_DELAYS[Math.min(attempt, VIDEO_RETRY_DELAYS.length - 1)];
